/**
 * The API client.
 *
 * Authentication is a cookie the browser sends automatically; there is no token
 * handling here, and there should never be. Errors arrive as a stable machine
 * code alongside a human message, so screens render from the code rather than
 * matching on prose.
 */

import type { PositionGroups } from "./position-exclusions";
// **Type-only, and it has to stay that way.** `npm test` is bare
// `node --experimental-strip-types` and `tsconfig` sets
// `allowImportingTsExtensions: false`, so a *value* import of a sibling here
// cannot be resolved — and because the tests import this module for
// `ApiError`, one would break every test file in `tests/`, not just its own.
// The awaiting itself therefore lives in the screens, over `discovery-job.ts`.
import type { DiscoveryJob } from "./discovery-job";

const BASE = "/api/v1";

export class ApiError extends Error {
  readonly code: string;
  readonly field: string | null;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, field?: string | null, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.field = field ?? null;
    this.details = details ?? {};
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      credentials: "same-origin",
      headers: init.body ? { "Content-Type": "application/json" } : {},
      ...init,
    });
  } catch (cause) {
    // `fetch` rejects — rather than answering — when the request never reached
    // the API: the connection dropped, DNS failed, the page went offline. It
    // throws a bare `TypeError`, which every caller then had to treat as a
    // fault in this frontend; it reached the Add Source scan as an unlabelled
    // "Something went wrong." and reached `retry.ts` as something it declines
    // to ask about again, which is backwards — this is the most retryable
    // failure there is.
    //
    // Status 0 because there is no status: nothing answered.
    throw new ApiError(
      0,
      "network_error",
      "The API could not be reached. Check your connection.",
      null,
      { cause: String(cause) },
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: any = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Vite's proxy (and some gateways) answer a failed upstream with HTML
      // or plain text. Matching on prose is wrong; naming the status is not.
      if (!response.ok) {
        throw new ApiError(
          response.status,
          "unknown_error",
          response.status === 502 || response.status === 504
            ? "The API did not answer. Check that the backend is running."
            : `Something went wrong (${response.status}).`,
        );
      }
      throw new ApiError(response.status, "unknown_error", "The API returned a response that was not JSON.");
    }
  }

  if (!response.ok) {
    const error = payload?.error ?? {};
    throw new ApiError(
      response.status,
      error.code ?? "unknown_error",
      // **Name the status when there is no message**, the way the non-JSON
      // branch above already does. A bodiless failure is not the API's: every
      // refusal it makes carries `{code, message}`, so an answer without one
      // came from something in between — on Netlify, the proxy rewrite that
      // gives any request twenty-six seconds and then answers for it.
      //
      // The bare sentence this used to throw is what the reviewer saw as
      // "Pass Rush: Something went wrong.", and it is the reason a 26-second
      // edge timeout read as a fault in PFF. A number would have said where to
      // look on the first morning rather than the third.
      error.message ?? `Something went wrong (${response.status}).`,
      error.field,
      error.details,
    );
  }
  return payload as T;
}

const get = <T,>(path: string) => request<T>(path);
const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) });
const put = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: "PUT", body: JSON.stringify(body) });
const patch = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const del = (path: string) => request<void>(path, { method: "DELETE" });

// --- Types -----------------------------------------------------------------

export type RunStatus = "queued" | "running" | "succeeded" | "failed" | "skipped" | "cancelled";

/**
 * What a repeated Identity Key inside one Feed means.
 *
 * `fail` is the default and the safe reading: two rows under one id are two
 * players the key cannot tell apart, and ranking one against a cohort holding
 * himself is worse than not publishing. `merge` is the declaration that this
 * publisher repeats rows — the first copy stands and the fold is counted on the
 * Run — and it is only true where the repeats are identical. Where they differ,
 * merging keeps one arbitrary row and discards the rest.
 */
export type RepeatedIdentity = "fail" | "merge";

export type Role =
  | "identity"
  | "player_name"
  | "team"
  | "position"
  /**
   * The season a player is draft-eligible, as the publisher reports it — PFF
   * ships `draft_season` on all thirteen of its reports.
   *
   * A role rather than one more carried field because it is a property of the
   * player: read once, reconciled across Feeds, and published under a fixed
   * `Draft Season` header instead of once per report under the path each one
   * used.
   */
  | "draft_season"
  | "stat"
  // Into the raw file untouched and no further: never percentiled, and
  // needing no Stat Dictionary entry. Its opposite is "ignored", which still
  // means discard.
  | "carried"
  | "ignored";

export type Direction = "higher_is_better" | "lower_is_better";
export type CredentialType =
  | "static_headers"
  | "cookie_jar"
  | "bearer_token"
  | "basic_auth"
  /** Stores an email and password and earns a session from them, rather than
   * presenting a secret that already exists. The only type with a lifecycle. */
  | "login_form";

export interface User {
  id: string;
  email: string;
  display_name: string;
  created_at: string;
}

/**
 * Which doors the sign-in screen should offer.
 *
 * Asked of the backend rather than compiled in, so a portal configured without
 * a Google client id does not render a button that cannot work. `password` is
 * the break-glass route and is off unless a deployment turns it on.
 */
export interface SignInMethods {
  google: boolean;
  google_client_id: string;
  hosted_domain: string;
  password: boolean;
}

/**
 * How a Feed's pages are reached. The four modes the Fetcher supports: follow a
 * published next-link (`auto`), read one page and stop (`none`), increment a
 * named query parameter using a counter the source publishes (`page_param`), or
 * increment it until a page comes back short (`page_until_short`).
 *
 * The fourth is for a publisher that describes nothing — MLS NEXT Pro answers
 * with a bare JSON array, so there is no next-link and no counter, and the only
 * evidence a walk has ended is a page shorter than the size that was asked for
 * (ADR-0041). Composed by a template rather than chosen here: the size has to
 * match the one in the URL, and the template is what wrote it.
 */
export type PaginationMode = "auto" | "none" | "page_param" | "page_until_short";

export interface Pagination {
  mode: PaginationMode;
  /** `page_param` and `page_until_short`. */
  param?: string | null;
  /** How many records a full page holds. `page_until_short` only, and required
   * there — it is the whole of what that mode knows. */
  size?: number | null;
}

export interface Feed {
  id: string;
  url: string;
  label: string | null;
  /**
   * Which sub-population of the league this Feed asks for — a Wyscout
   * conference. Null for every Feed of a league that has none, which is every
   * football league: PFF's thirteen reports and ESPN's four categories are
   * projections of one league rather than parts of it.
   *
   * Nothing renders it yet. It is composed by a Source Template rather than
   * typed, and it cannot be read out of a payload: a Wyscout record's
   * `domestic_competition_name` is the competition of the player's *current
   * club*, not the conference he was collected under.
   */
  conference: string | null;
  /**
   * Which club this Feed fetched, where the Feed *is* a club's roster. Composed
   * by a Source Template on exactly `conference`'s terms and for a sharper
   * reason: ESPN's soccer roster names the team once at the top of the payload
   * and puts no team field on the athlete, so no Binding can reach it and the
   * published `Team` column was empty in every row.
   *
   * Null for every publisher that puts the club on the record, where a Binding
   * reads it and this is never consulted.
   */
  team: string | null;
  position: number;
  /** GET is the default. POST carries a JSON `body` (GraphQL). */
  method?: "GET" | "POST";
  body?: Record<string, unknown> | unknown[] | null;
  /**
   * Empty for every Feed left on the default, which is most of them — an
   * absent `mode` is `auto`. Read it with `paginationOf`.
   */
  pagination: Pagination | Record<string, never>;
  etag: string | null;
  last_modified: string | null;
  /**
   * How many records this Feed's last read returned, or null for **nobody has
   * looked** — which is every Feed older than the column and the one state that
   * must never be reported as emptiness.
   *
   * It exists because the portal used to *declare* this. Four of Wyscout's
   * conferences carried a `measured empty` flag that unticked them and said
   * "it would fetch no players" under the box, and a live probe six weeks later
   * found the Pac-12 answering 148 (`add-source-usability/04`). A population is
   * a measurement with a date on it, so the portal records what it saw instead.
   */
  published_record_count: number | null;
  /** When that read happened. Null is "nobody has looked". */
  published_fields_read_at: string | null;
}

/** One thing an operator ticked, and the read that found nothing in it. */
export interface EmptyFeedGroup {
  /** The conference, or the Feed's own label where there is no conference. */
  name: string;
  /** How many of its Feeds were read and returned nothing. */
  feeds: number;
  /** The newest of those reads, so the claim can be dated on screen. */
  readAt: string;
}

/**
 * Which of a profile's Feeds came back with nobody in them, grouped.
 *
 * **Reported, never declared.** The template used to carry a `measured empty`
 * flag on four of Wyscout's conferences, which unticked them and rendered "it
 * would fetch no players" before anything was fetched; probing the live
 * endpoint found the Pac-12 answering 148 men and holding a women's competition
 * the same note said did not exist, so the default profiles were composing
 * without 348 players while the screen said there were none
 * (`add-source-usability/04`). This reads what the last fetch actually
 * returned.
 *
 * Three states, and telling them apart is the job. A null `read_at` is **nobody
 * has looked** and is silent — reporting it would accuse every Feed predating
 * the column, which is the same false claim one screen over. A nought is worth
 * saying. Anything above it is healthy.
 *
 * Grouped by `conference` because that is the grain an operator ticked: 175
 * Feeds over 25 conferences and seven projections, so *"seven Feeds returned
 * nothing"* is a puzzle where *"Conference USA returned nothing"* is an
 * instruction. Feeds carrying no conference — every publisher but Wyscout —
 * group under their own label, which is the honest grain there.
 *
 * **Every read Feed in the group must be empty.** One projection returning
 * nothing beside six that return players is a different fault, and claiming the
 * conference is empty would send somebody to untick a box that is working.
 */
export function emptyFeedGroups(feeds: readonly Feed[]): EmptyFeedGroup[] {
  const groups = new Map<string, { read: number; empty: number; readAt: string }>();
  for (const feed of feeds) {
    // Unread Feeds are not evidence in either direction, so they are not
    // counted — a half-read conference is judged on the half that was read.
    if (!feed.published_fields_read_at || feed.published_record_count === null) continue;
    const name = feed.conference ?? feed.label ?? feed.url;
    const seen = groups.get(name) ?? { read: 0, empty: 0, readAt: "" };
    seen.read += 1;
    if (feed.published_record_count === 0) seen.empty += 1;
    // The newest read, so the claim carries the date it was true on.
    if (feed.published_fields_read_at > seen.readAt) seen.readAt = feed.published_fields_read_at;
    groups.set(name, seen);
  }
  return [...groups.entries()]
    .filter(([, seen]) => seen.read > 0 && seen.read === seen.empty)
    .map(([name, seen]) => ({ name, feeds: seen.empty, readAt: seen.readAt }));
}

/**
 * A Feed as the forms send it back.
 *
 * `conference` is **optional, and omitting it is load-bearing** rather than
 * lazy. The backend reads a missing `conference` key as "leave the stored one
 * alone" and an explicit `null` as "clear it" — because these forms re-send the
 * whole Feed list to change anything at all, so a rename that sent `null`
 * twenty-two times would wipe a division's worth of monitoring grain on an edit
 * nobody would think to check.
 *
 * So nothing hand-edited here ever sets it. The only thing that does is
 * `feedInputFrom`, which carries across what a Source Template composed and
 * leaves the key off entirely when the template composed nothing — which is
 * both of the other two publishers.
 */
export interface FeedInput {
  url: string;
  label?: string | null;
  conference?: string | null;
  /**
   * Optional on exactly the terms `conference` above is, and against the same
   * hazard: a missing key leaves the stored club alone, an explicit `null`
   * clears it. A profile rename that sent `null` for twenty-five roster Feeds
   * would empty the `Team` column of every published file again.
   */
  team?: string | null;
  method?: "GET" | "POST";
  body?: Record<string, unknown> | unknown[] | null;
  pagination?: Pagination;
  /**
   * How each record this Feed returns names one further address (ADR-0035).
   *
   * Optional on exactly the terms `conference` above is, and with the same
   * hazard behind it: the backend reads a missing key as "leave the stored one
   * alone" and an explicit `null` as "clear it". A rename that sent `null` for
   * twenty-five roster Feeds would stop every one of them expanding, and the
   * next Run would publish seven hundred names with no statistics beside them —
   * a plausible-looking file, which is the worst way for this to fail.
   */
  expansion?: Expansion | null;
  /**
   * How this Feed's coded statistics become names (ADR-0043).
   *
   * Optional on exactly the terms `expansion` above is, and the consequence of
   * getting it wrong is the same one a degree quieter. A Feed that silently
   * stopped decoding would not lose a fan-out, it would publish the right
   * 1,876 skaters with every one of their 141 statistics missing — and
   * nothing about the row count, the identities or the file's shape would say
   * so.
   */
  decoding?: Decoding | null;
  /**
   * What the client's scan saw this address publishing (ADR-0061): each path it
   * answered for, with the first few values it gave.
   *
   * Sent by Add Source, which reads one address per report *before* the profile
   * exists — so there is no Feed row to write it to at the moment it is
   * measured, and discovery's own write cannot reach it. With it, a profile
   * added today offers the per-report disclosure on its Edit Fields screen
   * immediately; without it, the first reviewer pays the serial re-read of
   * every report to learn what this scan already knows.
   *
   * Absent leaves whatever is stored alone, as `conference` above does. Unlike
   * it, there is no way to clear this: an empty value leaves the stored reading
   * standing too, because the only sensible write is a fresher reading and a
   * rename that re-sent every Feed as empty would otherwise throw away a
   * seventeen-leaderboard profile's whole field inventory.
   */
  published_fields?: Record<string, unknown[]>;
}

/**
 * How a Feed's coded statistics become names a Binding can reach.
 *
 * InStat publishes a statistic as `{p, o, v}`, where `p` is the metric and `o`
 * the strength situation — both foreign keys into a *different* endpoint, so
 * the name is not in the payload at all and no rule about shape could recover
 * it. `array_path` says which field holds the readings and `catalogue` names
 * the map the portal decodes them with. Composed by the Source Template and
 * never typed into a form: what an operator would have to know to fill it in
 * is that `Goals` is the pair `(2, 0)`.
 */
export interface Decoding {
  array_path: string;
  catalogue: string;
}

/**
 * How each record a Feed returns names one further address, as the composer
 * declares it.
 *
 * `url_template` is how ESPN soccer is reached: its roster publishes an athlete
 * id and no address anybody outside ESPN can fetch, so the address is a recipe
 * over `{value}` — the field at `url_path` — and `{season}`. Composed by the
 * Source Template and never typed into a form.
 */
export interface Expansion {
  url_path: string;
  url_template?: string | null;
}

/**
 * The stored pagination, read the way the Fetcher reads it.
 *
 * Every field the API accepts has to be carried across, not just the ones this
 * function was written knowing about: a field dropped here is a field the forms
 * cannot round-trip. `size` arrived with `page_until_short` and was left out,
 * which cost the mode both of its uses at once — MLS NEXT Pro composes
 * `size: 100` and Add Source saved without it, so the server refused the only
 * template that needs the mode; and the Feed editor read a saved size back as
 * blank, so renaming a source saved `size: null` over it.
 */
export function paginationOf(feed: { pagination: Feed["pagination"] }): Pagination {
  const stored = feed.pagination as Pagination;
  return {
    mode: stored.mode ?? "auto",
    param: stored.param ?? null,
    size: stored.size ?? null,
  };
}

/**
 * A Source Template's declaration of one input.
 *
 * The form renders from this and from nothing else. A publisher-specific field
 * here would be the beginning of a form written per source, which is the thing
 * Source Templates exist to avoid — so if a new template needs a change in this
 * file, the declaration model has not delivered what it claims.
 */
export type TemplateParameterKind = "single_choice" | "multiple_choice" | "number";

export interface TemplateOption {
  value: string;
  label: string;
  hint: string | null;
  /**
   * What choosing this option means for other parameters, by their key.
   *
   * A publisher's menu can span endpoint surfaces whose facts differ: ESPN's
   * eight leagues share one Season field — the key is pinned, because a
   * profile's season rollover writes back to it — and its football leagues are
   * a season behind its soccer ones. So the league carries the season, and
   * picking one moves the box. `applyImplied` is where the form applies it, and
   * the server applies the same thing to anything omitted.
   */
  implies: TemplateValues;
  /**
   * Which sport choosing this composes into, or null where the template spans
   * only one sport — which is every option of every publisher but ESPN.
   *
   * **Declared by the server, never worked out here.** Which of ESPN's two
   * endpoint surfaces answers is decided by the league (ADR-0042), and the only
   * thing that knows which league is which is the composer; a list of soccer
   * league keys kept in the frontend would go stale the first time a league was
   * added. `test_source_templates.py` holds every declaration to the sport its
   * option actually composes, so this cannot quietly drift from the truth.
   *
   * `optionsFor` is the one reader: it narrows a parameter's menu by the sport
   * the Source menu above was filtered to. Optional on the type so a portal
   * served by an older backend reads it as "declares nothing", which
   * `optionsFor` treats as showing under every filter.
   */
  sport?: string | null;
  /**
   * The choices this option cannot be picked under, or null for available
   * whatever was chosen above it — nearly every option of nearly every
   * publisher.
   *
   * `TemplateParameter.applies_when` is the same idea one grain coarser: it
   * says a whole parameter is not asked. This is the option that is off the
   * menu while the parameter is still asked, which `options` cannot express by
   * itself because it is one static list that cannot vary with an earlier
   * choice.
   *
   * Wyscout's conference menu is why it exists (`add-source-usability/04`): six
   * of its thirty-three conferences are published for women only and three for
   * men only, so a men's profile was shown a tickable **Big Sky** that said
   * nothing until the compose button refused it.
   *
   * Optional on the type so a portal served by an older backend reads it as
   * "declares nothing" — every row available, which is what that backend meant.
   */
  unavailable_for?: TemplateOptionRule | null;
}

/**
 * Some of an earlier parameter's values, and what an option says under them.
 *
 * `values` is the choices the rule applies to; `message` is the line the server
 * refuses with. Nothing here composes one, and a frontend that wrote *"not
 * available for Men"* itself would have to be taught each publisher's
 * vocabulary — and would then disagree with the words the compose endpoint
 * uses. See `app/domain/templates.py`'s `OptionRule`.
 *
 * **Never a statement about a population.** A second rule briefly existed for
 * an option that was available and had been *measured empty*, and it rendered
 * "it would fetch no players" under four of Wyscout's conferences; two of the
 * four were wrong by the time anybody read them. What a publisher currently
 * holds is reported by the Run that fetched it, never by a menu.
 */
export interface TemplateOptionRule {
  parameter: string;
  values: string[];
  message: string;
}

/**
 * When the parameter carrying this is asked at all.
 *
 * The rule, not the answer — the answer changes as the form is filled in, and
 * the form is the only thing that knows what has been chosen yet. `parameter`
 * names another parameter and `values` are the choices this one applies to.
 */
export interface TemplateApplicability {
  parameter: string;
  values: string[];
}

export interface TemplateParameter {
  key: string;
  label: string;
  kind: TemplateParameterKind;
  hint: string | null;
  options: TemplateOption[];
  /** What the form starts on. A string, a list of them, or a number. */
  default: TemplateValue;
  /** `number` only. */
  minimum: number | null;
  maximum: number | null;
  /**
   * When this parameter is asked, or null for always.
   *
   * ESPN is why it exists: one menu entry over two endpoint surfaces, so its
   * soccer leagues are never asked for American football categories and the
   * season type is labelled for the surface that answers it. Nothing in this
   * file or in `TemplateParameters` knows that — the rule is read, never
   * branched on.
   */
  applies_when: TemplateApplicability | null;
}

/**
 * The half of a template the parameter machinery actually reads.
 *
 * Named because there are now two kinds of template — a Source Template, which
 * composes Feeds, and a Transfer Portal Template, which composes none — and the
 * three functions below plus the `TemplateParameters` component work on the
 * declarations rather than on what the template composes. Typing them to
 * `SourceTemplate` would have made the second menu either copy them or cast.
 */
export interface ParameterisedTemplate {
  parameters: TemplateParameter[];
}

export interface SourceTemplate extends ParameterisedTemplate {
  key: string;
  name: string;
  publisher: string;
  description: string;
  /** Whether a Run against this publisher will need a Credential Set. */
  needs_credential: boolean;
  /** The publisher's sign-in address, when the template knows it, so the form
   * asks only for an email and a password. Null means it has to be typed. */
  credential_login_url: string | null;
  /**
   * How this publisher's sign-in has to be performed, for the form to compose
   * onto the Credential Set it creates (ADR-0023). A publisher whose sign-in
   * carries bot protection needs a real browser; one signing in through a JSON
   * request needs a browser not to be used.
   *
   * Null on every template today, which leaves the deployment default to
   * decide — the state that keeps every existing source signing in as it did.
   * Wyscout's is the first declaration.
   */
  credential_acquirer: string | null;
  /** Which Credential Set the form creates when `needs_credential` is set.
   * Omitted/`login_form` is the historical default; `bearer_token` is a static
   * API key. Declared so the form stays publisher-agnostic. */
  credential_type?: "login_form" | "bearer_token" | null;
  /**
   * Whether this deployment already holds the secret this template needs, so
   * the form can stop asking for it.
   *
   * **A boolean, and the secret is not here.** The portal never returns secret
   * material, so what arrives is only that asking is unnecessary — the form
   * then posts `from_template` to `POST /credentials` and the server reads its
   * own configuration. PFF's developer key is the case: one account's key, set
   * once on the deployment, presented by every PFF profile in it.
   *
   * False — including for every template that names no setting — means the
   * form asks for the token exactly as it did before this existed.
   */
  credential_preconfigured?: boolean;
  /**
   * Every sport this template can compose a profile into, as the naming
   * standard spells them. One for every publisher but ESPN, whose single entry
   * spans a football surface and a soccer one.
   *
   * Three readers, and one of them reads it deliberately *not*:
   *
   * - **The Sport control does not read it**, and takes its answer off the
   *   composition instead. See `sportsOffered`.
   * - **Neither does the League menu**, which is now the first question Add
   *   Source asks and narrows by the sport each *offer* composes. See
   *   `LeagueOffer` on the server for why a per-template union is too coarse
   *   there: it answers *football and soccer* for ESPN, where the question is
   *   which of ESPN's eight leagues is which.
   * - **The server** falls back to it when a stored profile's parameters no
   *   longer validate, so saving stays possible against a template whose options
   *   have moved (ADR-0045).
   */
  sports?: string[];
  parameters: TemplateParameter[];
}

/**
 * The sports the Sport control offers, given what is composing.
 *
 * Three answers, and the middle one is the whole rule:
 *
 * - **A pasted URL offers all six.** There is no template, so the portal cannot
 *   know what the address publishes and the operator has to say.
 * - **A composed profile offers exactly the sport it composes** — one option,
 *   always. Not the template's whole range: ESPN spans football and soccer, but
 *   an ESPN profile *on the NFL* fetches football and nothing else, and
 *   offering soccer beside it would offer an answer its own Feeds contradict.
 *   The day ESPN's NFL surface publishes a second sport, the composer says so
 *   and this grows without being edited.
 * - **A template with no composition yet offers nothing**, and the caller hides
 *   the control rather than rendering an empty or wrongly-filled dropdown. The
 *   sport is not unknown-and-choosable at that moment, it is simply not
 *   established, and a required field showing a guess is worse than one that
 *   has not appeared.
 *
 * Read from the composition rather than from `SourceTemplate.sports` for the
 * reason the server's own check is: the sport is decided by the *league*, which
 * is a parameter, so a per-template list cannot tell one configuration from
 * another (ADR-0045).
 *
 * Nothing on Add Source reads that declaration any more. The Source menu it
 * used to filter is gone — the screen asks for a league first, and the league
 * menu carries the sport each offer composes rather than a union over the
 * publisher. What still reads `SourceTemplate.sports` is `profiles.py`'s
 * fallback, for a stored profile whose parameters no longer validate.
 *
 * Exported and free of React so `tests/` can reach it: the frontend runs its
 * tests as bare `node` over `tests/`, so anything worth asserting has to be
 * callable without a renderer.
 */
export function sportsOffered(
  route: "template" | "url",
  composed: TemplateComposition | null | undefined,
): readonly string[] {
  if (route === "url") return SPORTS;
  const sport = composed?.defaults?.sport;
  return typeof sport === "string" && sport !== "" ? [sport] : [];
}

/**
 * One way a publisher reaches a league.
 *
 * Several per source only where the publisher's menu distinguishes something
 * the league name does not — Cerebro's season scopes, which compose one league
 * name between them. The form shows the first and leaves the rest to the
 * parameter control it seeds; the whole list travels so the menu's search can
 * find a scope by the year the publisher calls it.
 */
export interface LeagueChoice {
  label: string;
  /** Parameter values as `composeTemplate` accepts them. */
  selects: TemplateValues;
}

/** One publisher of one league, as the Source dropdown needs it. */
export interface LeagueSource {
  template_key: string;
  template_name: string;
  publisher: string;
  /** So the menu can say a source is behind a sign-in before it is chosen. */
  needs_credential: boolean;
  /** `choices[0].selects` — what the form seeds when this source is picked. */
  selects: TemplateValues;
  choices: LeagueChoice[];
}

/**
 * One league, and every source that publishes it.
 *
 * The source catalogue read the other way round, which is the order the Add
 * Source form now asks its first question in: an operator knows they want NCAA
 * Division II football and not which of ten publishers carries it — and two of
 * them do. See `app/domain/league_menu.py` for what the inversion decides, and
 * `src/league-menu.ts` for what this screen does with it.
 */
export interface LeagueMenuEntry {
  /** `league_slug` of the name below: what two publishers of one league meet on. */
  key: string;
  /**
   * The canonical league name, exactly as a composer emits it and therefore
   * exactly as the saved profile will carry it.
   *
   * Shown beside `display` rather than instead of it: this is what the Sources
   * table prints for the resulting profile and what its published filenames are
   * built from, so a menu that hid it would hide the string the operator ends
   * up working with.
   */
  league: string;
  /**
   * What the row leads with.
   *
   * Thirty of the fifty-four canonical names are Cerebro's and are slugs, so a
   * menu leading with `league` opened on `adidas_3ssb_15u` over
   * `adidas_3ssb_16u` and read as a database column. This is the publisher's
   * own name for the league where there is one, and a declared one where
   * neither reads alone — resolved on the server, because two publishers of one
   * league must lead with the same name or the menu reads as two leagues.
   */
  display: string;
  sport: string;
  sources: LeagueSource[];
}

export type TemplateValue = string | string[] | number;

export type TemplateValues = Record<string, TemplateValue>;

/**
 * One Feed a Source Template built.
 *
 * Every field except `shape` is a `FeedInput` field, spelled as `FeedInput`
 * spells it, which is what lets `feedInputFrom` hand it straight to the create
 * endpoint. The forms used to rebuild a composed Feed field by field instead,
 * and that is how `conference` — composed by the only thing that can know it,
 * and the thing the per-conference counts are made of — was dropped between the
 * two. `shape` is advice to the field scan rather than part of a Feed, and
 * `feedInputFrom` drops it.
 */
export interface ComposedFeed {
  url: string;
  label: string;
  /**
   * Which sub-population of the league this Feed asks for, where the publisher
   * has them. Null for a publisher whose Feeds are not sub-populations.
   */
  conference: string | null;
  /**
   * Which club this Feed fetched, where the Feed is one club's roster. Null for
   * a publisher whose Feeds are not clubs, which is all of them but ESPN
   * soccer.
   */
  team: string | null;
  /**
   * Which column set this Feed carries. Feeds sharing a shape publish the same
   * fields, so the field scan reads one address per distinct shape instead of
   * one per Feed — seven requests for Wyscout rather than a hundred and
   * fifty-four, and every field found either way. Null is read as one shape per
   * Feed, which scans more addresses rather than fewer.
   */
  shape?: string | null;
  /** How the Feed is requested. GET for every publisher before GraphQL. */
  method?: "GET" | "POST";
  /** The JSON document a POST Feed sends. Null for GET. */
  body?: Record<string, unknown> | unknown[] | null;
  pagination: Pagination | Record<string, never>;
  /**
   * How each record this Feed expands. Empty for every publisher whose Feeds
   * are one retrievable endpoint, which is all of them but ESPN soccer.
   */
  expansion?: Expansion | Record<string, never>;
  /**
   * How this Feed's coded statistics become names. Empty for every publisher
   * whose statistics are fields, which is all of them but InStat.
   */
  decoding?: Decoding | Record<string, never>;
}

/**
 * A composed Feed as a create or update body wants it.
 *
 * The one place `FeedInput.conference` is ever set, and it is set only when the
 * template named one — see that field's comment for why an explicit `null` is
 * not a harmless way to say "no conference".
 *
 * `pagination` is normalised rather than omitted, and it is the *template's*
 * choice being carried rather than a screen default being applied: ESPN
 * publishes a next link on a host that answers the portal with 403, so its
 * Feeds have to count pages. Read through `paginationOf` so this file holds one
 * statement of what an empty pagination means, not two.
 */
/**
 * The smallest set of addresses whose fields are all of the source's fields.
 *
 * One Feed per distinct `shape`, in composed order. Feeds sharing a shape carry
 * the same columns, so reading a second one costs a request and learns nothing
 * — which is the whole difference between seven requests and a hundred and
 * fifty-four on a Wyscout profile.
 *
 * **A Feed declaring no shape is its own shape.** That is the safe direction: a
 * template whose composer has not been taught to name its projections gets
 * every one of its Feeds read, which is slow and complete rather than fast and
 * short. The failure this function exists to prevent is the short one.
 */
export function feedsToScan(feeds: ComposedFeed[]): ComposedFeed[] {
  const seen = new Set<string>();
  const chosen: ComposedFeed[] = [];
  const shapeKey = (feed: ComposedFeed, index: number) =>
    feed.shape ? `shape:${feed.shape}` : `feed:${index}`;
  feeds.forEach((feed, index) => {
    // Index rather than a shared sentinel, so two unshaped Feeds are two shapes
    // rather than one. `null` and `""` are both "not declared".
    //
    // **`field-reports.ts` composes this same key**, because it has to decide
    // which Feeds each of these reads answers for (ADR-0061) — and the two
    // reading "same shape" differently would file one report's field list
    // against a Feed publishing something else. Written out twice rather than
    // shared: `tsconfig` sets `allowImportingTsExtensions: false`, and a value
    // import from here into `src/` cannot be resolved by `npm test`, which is
    // bare node. `tests/field-reports.test.ts` asserts the two agree instead.
    const shape = shapeKey(feed, index);
    if (seen.has(shape)) return;
    seen.add(shape);
    chosen.push(feed);
  });
  return chosen;
}

export function feedInputFrom(feed: ComposedFeed): FeedInput {
  return {
    url: feed.url,
    label: feed.label,
    ...(feed.conference ? { conference: feed.conference } : {}),
    // And the club, on identical terms: only where the template composed one,
    // so the missing key keeps meaning "leave the stored team alone".
    ...(feed.team ? { team: feed.team } : {}),
    ...(feed.method === "POST" ? { method: feed.method, body: feed.body ?? null } : {}),
    // Only where the template composed one, so the missing key keeps meaning
    // "leave the stored expansion alone" — see `FeedInput.expansion`.
    ...(feed.expansion && "url_path" in feed.expansion
      ? { expansion: feed.expansion as Expansion }
      : {}),
    // And the decoding, on identical terms. Dropped here, an InStat profile
    // saves a Feed that fetches every skater and publishes none of their
    // statistics — which succeeds.
    ...(feed.decoding && "catalogue" in feed.decoding
      ? { decoding: feed.decoding as Decoding }
      : {}),
    pagination: paginationOf(feed),
  };
}

/** What choosing a template and filling it in produces. */
export interface TemplateComposition {
  template_key: string;
  parameters: TemplateValues;
  feeds: ComposedFeed[];
  /**
   * A partial `SourceProfileInput`: the fields this publisher already knows the
   * answer to — its Identity Key, its Record Path, the columns whose absence
   * means a lapsed session. Named as the create endpoint names them, so the
   * form fills fields in rather than translating between two vocabularies.
   */
  defaults: Partial<SourceProfileInput>;
}

/**
 * Whether a parameter is asked, given what has been chosen so far.
 *
 * One function for rendering and for describing a saved profile, and the same
 * rule the server validates by, so the questions on screen and the questions
 * the API accepts cannot drift apart.
 */
export function parameterApplies(
  parameter: TemplateParameter,
  values: TemplateValues,
): boolean {
  const rule = parameter.applies_when;
  if (!rule) return true;
  const chosen = values[rule.parameter];
  return typeof chosen === "string" && rule.values.includes(chosen);
}

/** Only the parameters this set of values actually asks for. */
export function applicableParameters(
  template: ParameterisedTemplate,
  values: TemplateValues,
): TemplateParameter[] {
  return template.parameters.filter((parameter) => parameterApplies(parameter, values));
}

/**
 * What the choices in `chosen` imply for `key`, or undefined if none do.
 *
 * Only the choices actually **in** `chosen`, and in declaration order so a
 * template narrowing an implication further down its list wins. Both halves
 * mirror the server's `SourceTemplate.implied` deliberately: an implication the
 * form resolved differently from the compose endpoint would be a Feed list on
 * screen that is not the one about to be saved.
 */
export function impliedValue(
  template: ParameterisedTemplate,
  key: string,
  chosen: TemplateValues,
): TemplateValue | undefined {
  let implied: TemplateValue | undefined;
  for (const parameter of template.parameters) {
    if (parameter.kind !== "single_choice") continue;
    const value = chosen[parameter.key];
    if (value === undefined) continue;
    const option = parameter.options.find((o) => o.value === value);
    if (option && key in option.implies) implied = option.implies[key];
  }
  return implied;
}

/**
 * A parameter's options, less the ones the choices so far have taken off the menu.
 *
 * **A row an earlier choice has ruled out is not listed at all.** Disabling it
 * and explaining why was the first attempt and it was still the publisher's
 * internal shape on screen: under *Men*, six conferences Wyscout publishes for
 * women only sat in the list saying so, which is six rows of apology where the
 * honest answer is that the menu has twenty-four entries. The client's words
 * were *"only the available options should be listed"*.
 *
 * **Except one that is already chosen**, which is `optionsFor`'s second rule and
 * the same reasoning: a menu cannot discard work. Unreachable through the form,
 * which unticks a row as the choice that blocked it changes
 * (`withAvailableChoices`); reachable by recomposing a **saved** profile after a
 * publisher withdrew a competition, and there a hidden row is a compose that
 * fails naming a conference not on screen. It stays tickable, so the one thing
 * to do with it — clear it — is possible.
 *
 * Every kind, because the rule is about an option rather than about a control —
 * and a single choice is why "unless chosen" is not optional: a `<select>` whose
 * value matches none of its options renders blank, which is this frontend's
 * most-repeated defect (`field-mapping-usability/01`).
 *
 * Declaration order is kept, and the same array comes back untouched where no
 * option declares a rule — every option of every publisher but Wyscout.
 */
export function offeredOptions(
  parameter: TemplateParameter,
  values: TemplateValues,
): readonly TemplateOption[] {
  const options = parameter.options ?? [];
  if (!options.some((option) => option.unavailable_for)) return options;
  const value = values[parameter.key];
  const chosen = new Set(
    (Array.isArray(value) ? value : value === undefined ? [] : [value]).map(String),
  );
  return options.filter(
    (option) => !ruleApplies(option.unavailable_for, values) || chosen.has(option.value),
  );
}

/**
 * Whether one of an option's rules applies, given the choices made so far.
 *
 * The same shape as `parameterApplies` and deliberately the same strictness: a
 * value that is not a string — an unanswered parameter, or a multiple choice
 * where a rule names a single one — satisfies nothing.
 *
 * A type predicate rather than a plain boolean so that `true` narrows away the
 * null: a caller then reads `rule.message` directly instead of asserting it is
 * there, and `src/` keeps its count of non-null assertions at nought.
 */
function ruleApplies(
  rule: TemplateOptionRule | null | undefined,
  values: TemplateValues,
): rule is TemplateOptionRule {
  if (!rule) return false;
  const chosen = values[rule.parameter];
  return typeof chosen === "string" && rule.values.includes(chosen);
}

/**
 * `values`, less any ticked option an earlier choice has just taken off the menu.
 *
 * Applied after a single choice changes, where `implies` is applied, because
 * that is the moment a row can stop being available while still being ticked.
 * For Wyscout it is belt and braces — both of its gender options imply a
 * conference set of their own, so switching replaces the list outright — and
 * belt and braces is the right amount: an implication is a **default**, so a
 * template whose earlier choice implies nothing would otherwise carry a ticked
 * row into a compose that refuses it, which is the after-the-fact red text this
 * issue was filed about.
 *
 * **Multiple choices only.** A single choice is never cleared here: a
 * `<select>` whose value matches none of its options renders *blank*, which is
 * this frontend's most-repeated defect
 * (`field-mapping-usability/01` has the sightings), and a blank required
 * control leaves a form that cannot compose where a dropped checkbox leaves one
 * that can.
 *
 * Inapplicable parameters are skipped for the reason `TemplateParameters`
 * leaves their values standing — switching a choice away and back must not lose
 * what was filled in — and the server drops them on the way past. The same
 * object comes back when nothing changed, so every publisher whose options
 * declare no rule pays nothing for this.
 *
 * Every rule is read against `values` rather than against the result being
 * built, deliberately: a rule names a *single* choice and this only ever edits
 * multiple ones, so the two can never differ — and judging each parameter
 * against the same settled state is what stops the order of the list mattering.
 */
export function withAvailableChoices(
  template: ParameterisedTemplate,
  values: TemplateValues,
): TemplateValues {
  let next = values;
  for (const parameter of template.parameters) {
    if (parameter.kind !== "multiple_choice") continue;
    if (!parameterApplies(parameter, values)) continue;
    const ticked = values[parameter.key];
    if (!Array.isArray(ticked)) continue;
    const blocked = new Set(
      parameter.options
        .filter((option) => ruleApplies(option.unavailable_for, values))
        .map((option) => option.value),
    );
    if (blocked.size === 0) continue;
    const kept = ticked.filter((value) => !blocked.has(String(value)));
    if (kept.length === ticked.length) continue;
    next = { ...next, [parameter.key]: kept };
  }
  return next;
}

/** One row of a parameter's menu, and whether the sport filter excludes it. */
export interface OptionRow {
  option: TemplateOption;
  offFilter: boolean;
}

/**
 * A parameter's choices, narrowed by the sport the Source menu was filtered to.
 *
 * **The same filter one control further in as `narrowLeagues`, and
 * deliberately the same two rules.** That one decides which leagues a sport
 * filter leaves in the League menu; this decides which of the chosen
 * publisher's own choices it leaves underneath. Both are the same question —
 * *could this be the thing I am looking for* — asked of a league and then of
 * the parameter that reaches it.
 *
 * It exists because the answer used to be "all of them". ESPN is one menu entry
 * over a football surface and a soccer one, so filtering the Source menu to
 * football and picking ESPN left a League menu offering MLS, NWSL and the Saudi
 * Pro League: four clicks from a soccer profile built under a football filter,
 * with every check green, because the sport of an ESPN profile follows from its
 * league and the league had just been chosen out of the wrong half of a list.
 *
 * - **It cannot hide a choice on a guess.** An option declaring no sport is
 *   shown under every filter. That is every option of every single-sport
 *   publisher — the server declares the field only where a template spans more
 *   than one — and absence of a declaration is not evidence of a mismatch.
 * - **It cannot discard work.** `chosen` stays in the list wherever it sits,
 *   marked `offFilter` when the filter excludes it. A `<select>` whose value
 *   matches none of its options renders **blank**, which is this frontend's
 *   most-repeated defect (`field-mapping-usability/01` has the sightings), and
 *   it would blank a control sitting over a season, a set of categories and a
 *   list of composed Feeds that all belong to the value just hidden.
 * - **A filter no choice satisfies is a filter with nothing to say**, and the
 *   whole menu comes back. That case is reachable: the Source menu keeps a
 *   *chosen* publisher under a filter it does not cover — on the first rule,
 *   deliberately — so ESPN can sit under a hockey filter, and narrowing its
 *   League menu by hockey would otherwise leave one locked row that cannot even
 *   be changed to College Football. Nothing is marked in that case, because the
 *   row above has already said this publisher is not hockey and marking all
 *   eight would be saying it eight more times.
 *
 * Declaration order is kept, because that order is the publisher's.
 */
export function optionsFor(
  parameter: TemplateParameter,
  sport: string,
  chosen: TemplateValue | undefined,
): readonly OptionRow[] {
  const options = parameter.options ?? [];
  const everything = () => options.map((option) => ({ option, offFilter: false }));
  if (sport === "") return everything();
  // A parameter none of whose options declare a sport is not a parameter this
  // filter knows anything about — every option of every single-sport publisher
  // — and the loop below would return it unchanged anyway. Returning early says
  // so, and makes the behaviour provably identical for those templates.
  if (!options.some((option) => (option.sport ?? null) !== null)) return everything();

  const rows: OptionRow[] = [];
  for (const option of options) {
    const declared = option.sport ?? null;
    if (declared === null || declared === sport) rows.push({ option, offFilter: false });
    else if (option.value === chosen) rows.push({ option, offFilter: true });
  }
  // Nothing the filter allows: it has no purchase on this parameter, so it does
  // not get to reduce it to the one row somebody had already picked.
  return rows.some((row) => !row.offFilter) ? rows : everything();
}

/**
 * The starting values of a template, read straight off its declaration.
 *
 * Walked in declaration order rather than mapped, because applicability and
 * implications are both resolved against the choices already made — so what a
 * parameter starts on can depend on an earlier one, and its being asked at all
 * can too. This is the server's own algorithm for composing with nothing
 * submitted, which is what makes the form open showing what the compose endpoint
 * would answer with.
 *
 * The applicable parameters only, for the same reason a composition holds only
 * those: they are the questions this set of choices asks. Switching a choice
 * away and back does not lose what was filled in — `TemplateParameters` leaves
 * an inapplicable value standing rather than dropping it, and the server drops
 * it on the way past.
 */
export function templateDefaults(
  template: ParameterisedTemplate,
  /**
   * The sport the Source menu was filtered to, or "" for none.
   *
   * A declared default that the filter excludes is replaced by the first choice
   * it allows. Otherwise picking ESPN under a *soccer* filter opens the form on
   * the NFL — because `nfl` is the declaration's default and the declaration
   * knows nothing about a filter — and the operator's first act is to undo the
   * answer they just gave one control up.
   *
   * Only ever a single choice, and only where the filter can decide: a
   * parameter whose options declare no sport is left exactly as it was, and so
   * is one no option satisfies. A required control holding a value the filter
   * disagrees with is recoverable, and `optionsFor` marks it; one holding
   * nothing is a blank `<select>` over a form that is already composing.
   */
  sport: string = "",
  /**
   * Values that are already settled, which beat both the declaration's default
   * and the sport filter.
   *
   * This is how a league chosen one control up reaches the form. The league
   * menu knows that PFF's Division II is `{division: "ncaa-d2"}` — the server
   * said so, having read it off the same constant the composer does — and the
   * form has no business re-deriving it from a parameter whose key it would
   * have to guess at.
   *
   * **Seeded in the same loop rather than merged over the result**, which is
   * the only part of this worth reading twice. A seeded value is in `chosen`
   * before the parameters below it resolve, so what it *implies* reaches them:
   * seeding ESPN's league as `mls` carries soccer's season into the Season box,
   * where merging afterwards would leave football's year sitting beside a
   * soccer league with every control looking filled in. ADR-0045's rollover
   * writes back to that same box.
   *
   * A key naming a parameter the template does not declare, or one that does
   * not apply, is ignored rather than refused — the seed describes a league,
   * and a league outliving the template it was declared on is a menu one poll
   * out of date, not a form that should refuse to open.
   */
  seed: TemplateValues = {},
): TemplateValues {
  const chosen: TemplateValues = {};
  for (const parameter of template.parameters) {
    if (!parameterApplies(parameter, chosen)) continue;
    const settled = seed[parameter.key];
    if (settled !== undefined) {
      chosen[parameter.key] = settled;
      continue;
    }
    const implied = impliedValue(template, parameter.key, chosen);
    const fallback = implied === undefined ? parameter.default : implied;
    chosen[parameter.key] = startingValue(parameter, fallback, sport);
  }
  return chosen;
}

/**
 * `fallback`, or the first choice the sport filter allows when it excludes it.
 *
 * Exported because a parameter can start twice: once when the form opens
 * (`templateDefaults`) and once when a choice brings a parameter into a form
 * that was not asking it (`TemplateParameters`'s `set`). Those two are the same
 * question — *what does this control start on* — and answering it in two places
 * is how one of them ends up ignoring the filter.
 *
 * In `templateDefaults` it runs in the same loop that resolves implications,
 * which is what keeps the rest of the form right: the choice settled here is in
 * `chosen` before the next parameter resolves, so a league moved from the NFL to
 * a soccer league carries soccer's season with it rather than leaving
 * football's in the box.
 */
export function startingValue(
  parameter: TemplateParameter,
  fallback: TemplateValue,
  sport: string,
): TemplateValue {
  if (sport === "" || parameter.kind !== "single_choice") return fallback;
  const allowed = optionsFor(parameter, sport, fallback).filter((row) => !row.offFilter);
  // `optionsFor` hands back the whole menu where the filter decides nothing, so
  // this is the same question asked once: is what the declaration chose among
  // the rows the control is about to show?
  if (allowed.some((row) => row.option.value === fallback)) return fallback;
  return allowed[0]?.option.value ?? fallback;
}

export interface CredentialSummary {
  id: string;
  name: string;
  credential_type: CredentialType;
  /** How it signs in, or null for the deployment default. Not a secret — the
   * name says which mechanism runs, never what it presents. Nothing renders it
   * yet; it is here so a screen that wants to say "this one needs a browser"
   * does not need an API change first. */
  session_acquirer: string | null;
  /** How the asynchronous sign-in is going (ADR-0033), or null for a Credential
   * Set that never started one — every type that presents an existing secret,
   * and every `login_form` credential saved against a profile rather than
   * created on its own. Null is the absence of a lifecycle, not a failed one. */
  sign_in_status: SignInStatus | null;
  /** A Failure Reason's code and the acquirer's own sentence, where the
   * sign-in failed. On the record rather than in a response body, because the
   * request that started the sign-in answered long before the outcome
   * existed. */
  sign_in_code: string | null;
  sign_in_message: string | null;
  last_succeeded_at: string | null;
  updated_at: string;
  updated_by: string | null;
}

export type SignInStatus = "in_progress" | "succeeded" | "failed";

/** A sign-in that has answered, one way or the other. What polling waits for. */
export function signInSettled(status: SignInStatus | null | undefined): boolean {
  return status === "succeeded" || status === "failed";
}

export interface RunSummary {
  id: string;
  status: RunStatus;
  phase: string | null;
  trigger: string;
  records_processed: number;
  /**
   * How far through its Feeds the Run is. The record count stays at zero until
   * parsing, so on a thirteen-report source this is the only thing that moves
   * for the minutes the fetch takes. Both are zero on Runs recorded before the
   * counter existed.
   */
  feeds_fetched: number;
  feeds_total: number;
  /**
   * How far through its record expansions the Run is (ADR-0035). A Feed whose
   * records each name a further address is no longer bounded by its
   * pagination: a roster of 700 players costs 700 further requests, over which
   * `feeds_fetched` has already reached its total. Zero for every Feed
   * declaring no expansion, which is every publisher but ESPN soccer.
   */
  expansions_done: number;
  expansions_total: number;
  /** Records whose derived request failed, and which carry no derived fields. */
  expansions_failed: number;
  player_count: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  failure_code: string | null;
  failure_message: string | null;
}

/**
 * The sport folders the data file naming standard allows, and nothing else.
 * Gender and division belong in the league name, never in the sport.
 */
export const SPORTS = [
  "football",
  "soccer",
  "basketball",
  "baseball",
  "hockey",
  "lacrosse",
] as const;

/** One of D20's gate checks, and what it found. */
export interface GateCheck {
  name: string;
  /**
   * Three values, not two. A failed Run cannot be asked whether its player
   * count was in band, and showing an unmeasurable check as passing is how a
   * gate becomes decorative.
   */
  outcome: "passed" | "failed" | "not_measured";
  detail: string;
}

/** Which refusal a gate verdict is. */
export type GateCode =
  | "ready"
  | "no_dry_run"
  | "dry_run_retired"
  | "checks_failed"
  | "roster_turned_over";

/** Whether this profile may be promoted onto a schedule, and why not. */
export interface Gate {
  ready: boolean;
  reason: string;
  /**
   * Which refusal this is, named by the server.
   *
   * `reason` is prose the server is free to reword, so it is rendered and
   * never matched on. This was inferred from which fields the payload
   * populated until a refusal arrived that no arrangement of them
   * distinguishes — `roster_turned_over` carries a dry run whose own checks
   * passed, because what turned over was measured by the Run that published.
   */
  code: GateCode;
  dry_run_id: string | null;
  dry_run_at: string | null;
  checks: GateCheck[];
}

/**
 * Something a schedule does not promise, carried as data.
 *
 * A screen can forget to mention a constraint; a field it renders cannot.
 */
export interface ScheduleLimitation {
  code: string;
  message: string;
}

export interface Schedule {
  /** Five cron fields, in UTC. Null for a source somebody runs by hand. */
  cron: string | null;
  enabled: boolean;
  timezone: string;
  enabled_at: string | null;
  enabled_by: string | null;
  last_fired_at: string | null;
  next_run_at: string | null;
  gate: Gate;
  limitations: ScheduleLimitation[];
}

export interface SourceProfile {
  id: string;
  name: string;
  league: string;
  sport: string;
  season: number;
  season_active: boolean;
  identity_key: string[];
  /** What a repeated Identity Key inside one Feed means. See the type. */
  repeated_identity: RepeatedIdentity;
  scope: string | null;
  record_path: string | null;
  position_map: Record<string, string>;
  team_filter: Record<string, unknown> | null;
  eligibility_stat_key: string | null;
  eligibility_min_value: number;
  min_bound_stats: number;
  /**
   * Which ranking method this profile's Runs use, or null for the portal's
   * current one — which is what every profile that does not declare one gets.
   * Composed by a Source Template through its `defaults`, the same route the
   * Identity Key arrives by: which distributional model suits a statistic is a
   * fact about the publisher, since a percentage and a per-90 rate are not the
   * same shape.
   */
  ranking_method: string | null;
  required_columns: string[];
  min_players: number;
  /** The other end of D20's band. Zero means no ceiling. */
  max_players: number;
  request_budget: number;
  request_delay_ms: number;
  /** The Client Identity this profile presents, or null for the portal's
   * honest default (ADR-0015). */
  user_agent: string | null;
  /** Which Source Template composed these Feeds, or null for a pasted URL. */
  template_key: string | null;
  template_parameters: TemplateValues;
  /** §2A's Source Slug: where this league's data came from. */
  source_slug: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Who built this source. Everyone in the Workspace sees every profile
   * (ADR-0016), so this is what keeps shared visibility from costing the
   * ability to ask the right person about one.
   */
  created_by: string | null;
  /**
   * When a person last changed this source, and which person — null for a
   * profile edited before the portal recorded either.
   *
   * Not `updated_at`, which moves when the timer stamps a firing onto the row
   * and would report a scheduled source as edited every hour by nobody. The
   * server stamps these from signed-in requests only.
   */
  last_edited_at: string | null;
  last_edited_by: string | null;
  feeds: Feed[];
  credential: CredentialSummary | null;
  last_run: RunSummary | null;
  last_successful_run_at: string | null;
  binding_count: number;
  unreviewed_binding_count: number;
  schedule: Schedule | null;
}

// --- The transfer portal ----------------------------------------------------

/**
 * One publisher in the Transfer Portal menu.
 *
 * Deliberately carries the same `TemplateParameter` declarations a Source
 * Template does, so `TemplateParameters` renders both menus. The two offer
 * different kinds of thing — this composes no Feed, because a Transfer Portal
 * Profile has none — but a parameter is a parameter, and a second component
 * would be a second set of rules about what `applies_when` means.
 */
export interface TransferPortalTemplate extends ParameterisedTemplate {
  key: string;
  name: string;
  publisher: string;
  description: string;
  sports: string[];
}

export interface TransferPortalComposition {
  template_key: string;
  parameters: TemplateValues;
  /** Profile fields, named exactly as the create endpoint names them. */
  defaults: Record<string, unknown>;
}

export interface TransferPortalProfile {
  id: string;
  name: string;
  publisher: string;
  league: string;
  sport: string;
  season: number;
  page_url: string;
  transfers_url: string;
  page_size: number;
  name_match_threshold: number;
  request_budget: number;
  user_agent: string | null;
  template_key: string | null;
  template_parameters: TemplateValues;
  created_at: string;
  created_by: string | null;
  last_run: RunSummary | null;
  /**
   * When the timer will fire this profile next, or null when no cron is set.
   *
   * Computed whenever a cron is written down, switched on or not, so that
   * somebody who has typed an expression can see what it means before
   * promoting it. `schedule_enabled` is what says whether it will actually
   * fire.
   */
  next_run_at: string | null;
  schedule_enabled: boolean;
  /** Whether this portal is open — what makes an unchanged publisher a fault. */
  season_active: boolean;
}

export interface TransferPortalSchedule {
  cron: string | null;
  enabled: boolean;
  timezone: string;
  enabled_at: string | null;
  enabled_by: string | null;
  last_fired_at: string | null;
  next_run_at: string | null;
  limitations: ScheduleLimitation[];
}

export interface Run extends RunSummary {
  /**
   * Which profile this Run belongs to — exactly one is set, and the server
   * holds that in the database rather than by convention. Two fields rather
   * than one, because the two link to different screens.
   */
  source_profile_id: string | null;
  transfer_portal_profile_id: string | null;
  started_by: string | null;
  queued_at: string;
  cancel_requested: boolean;
  failure_hint: string | null;
  diagnostics: Record<string, unknown> | null;
  requests_made: number;
  requests_skipped: number;
  ranking_method: string | null;
  ranking_version: string | null;
  cohort_fallbacks: { canonical_position_group: string; stat_key: string; group_size: number }[];
  output_keys: Record<string, { stable: string }>;
  available_downloads: string[];
}

/**
 * A sport's pre-authored Stat Dictionary entries, installable in one action.
 *
 * Distinct from a Source Template: a template is a recipe for reaching *a
 * publisher*, while a Vocabulary describes what a sport's statistics mean
 * whoever publishes them.
 */
export interface Vocabulary {
  key: string;
  name: string;
  sport: string;
  description: string;
  entry_count: number;
  /** How many are already present — counted, so this describes what is there
   * rather than whether the button was once pressed. */
  installed_count: number;
}

export interface VocabularyInstall {
  key: string;
  sport: string;
  added: number;
  already_present: number;
}

/** One object a Run will write, as the Settings screen shows it. */
export interface PreviewedPath {
  kind: "raw" | "ranked" | "status" | "validation";
  label: string;
  /** Relative to the destination root — what the naming standard governs. */
  key: string;
  /** The same key with the destination in front of it. */
  location: string;
  /** Shared by every league in the sport rather than written per source. */
  per_sport: boolean;
}

export interface SourcePathPreview {
  source_profile_id: string | null;
  name: string;
  league: string;
  sport: string;
  season: number;
  paths: PreviewedPath[];
  /** Set when this source cannot compose a legal path at all. */
  error: string | null;
}

export interface StoragePreview {
  root: string;
  /** True when no source exists yet and the rows shown are examples. */
  illustrative: boolean;
  sources: SourcePathPreview[];
}

/**
 * The distributional shapes a statistic can have (ADR-0027), under the names
 * the pipeline's metric config spells them — kept rather than lengthened to
 * `gamma`/`gaussian` so the two stay diffable.
 */
export type Distribution = "gamm" | "beta" | "gaus";

/** One raw position label a Run published. Mirrors `PositionLabelOut`. */
export interface PositionLabel {
  label: string;
  canonical_position_group: string;
  players: number;
  /** Whether a map already answers for it. Served rather than inferred: a label
   *  deliberately mapped **to** `UNKNOWN` is mapped, and deriving it from the
   *  group would offer work already done. */
  mapped: boolean;
}

/**
 * What a source has published, and whether it has published at all.
 *
 * Two fields rather than a bare list because an empty list means two things
 * with opposite remedies: a source that has never run (run it), and one whose
 * last Run predates this record (run it *again*) — which is every Run stored
 * before the feature shipped.
 */
export interface PublishedPositionLabels {
  labels: PositionLabel[];
  published_run: boolean;
}

export interface StatEntry {
  id: string;
  /**
   * Which sport's vocabulary this entry belongs to (ADR-0017). An entry is
   * identified by sport and key together, because Direction is a property of
   * the statistic and two sports can disagree about it — `penalties` is penalty
   * flags and lower-is-better in football, penalty kicks and higher-is-better
   * in soccer.
   */
  sport: string;
  key: string;
  display_name: string;
  direction: Direction;
  /**
   * What a Binding made from this statistic should weigh in the Impact Factor,
   * as a percentage, unless the field review says otherwise.
   *
   * **A default for Bindings that do not exist yet, not a weight the ranker
   * reads.** `runner.py` builds its weight map from `Binding.weight` alone, so
   * filing one here cannot move a published file or an existing Binding — which
   * is what keeps it clear of ADR-0040, where the thing refused was a weight on
   * the entry that *ranked*. Discovery copies it onto a new Binding.
   *
   * Null is "nothing declared", which weighs 1. Not 1 written out: the two rank
   * identically and are different review states.
   */
  default_weight: number | null;
  /**
   * Which distributional shape this statistic has, for a ranking method that
   * fits one (ADR-0027) — a beta for a bounded proportion, a gamma for a
   * non-negative per-90 rate, a gaussian for a quantity that runs either way.
   * A property of the statistic on the same terms as the two fields above.
   *
   * Null for every football entry, which is every entry that predates the
   * method, and read as a gamma by the method itself. Nothing renders it yet:
   * soccer's ninety-one declarations arrive with the Vocabulary rather than
   * being typed one at a time.
   */
  distribution: Distribution | null;
  /**
   * This statistic is `per_90_of` per ninety minutes of `per_90_minutes_key`,
   * computed where nothing bound the entry directly (ADR-0036). Both name other
   * entries in the same sport's dictionary, and both are null together — a
   * numerator with no denominator derives nothing and is refused at the form.
   *
   * Served since the derivation existed and typed here only now, because the
   * editor shows them: a form that silently omitted a declaration the entry
   * holds would read as an entry that declares nothing. They are not editable
   * there — soccer's ninety-one arrive with a Vocabulary rather than one at a
   * time.
   */
  per_90_of: string | null;
  per_90_minutes_key: string | null;
  description: string | null;
  aliases: string[];
  /**
   * Canonical Position Groups this statistic does **not** rank. Empty is "no
   * exclusions", which is every entry that predates the column.
   *
   * A member of one of these groups takes no percentile for the statistic, his
   * value is kept out of the population it is scored against, and it is absent
   * from his Impact Factor rather than paying the midpoint into it — which is
   * the difference from ADR-0037's Carrier floor, where a statistic a Cohort
   * barely carries still scores every member at 0.5. His **raw** value is
   * untouched: only the modelled column is empty, the treatment an Unscored
   * Player already gets.
   *
   * On the entry rather than on the Binding, where a weight lives: a running
   * back is not ranked on passing yards whoever published the yardage, so it is
   * a property of the statistic on the same terms as Direction.
   */
  excluded_position_groups: string[];
}

/**
 * What a Source Profile would have to be edited for if this entry's key moved.
 *
 * `Binding.stat_key` names an entry by value with no foreign key — it cannot
 * have one, since an entry is identified by sport *and* key (ADR-0017) — so the
 * server carries every reference across in one transaction and a rename works.
 * This is what makes it a *decision*: a rename republishes every file bound to
 * the statistic with its columns in a new order, because the published column
 * list is sorted on the key rather than on the display name.
 *
 * Counted within the entry's own sport, as the cascade is. A football profile is
 * not usage of a soccer entry however the key reads.
 */
export interface StatUsage {
  /** Bindings naming the key, through `stat_key` or a split rule's components. */
  binding_count: number;
  /** Profiles using it as their eligibility statistic, where leaving it behind
   *  makes every player read as not having played. */
  eligibility_count: number;
  /** How many of `profiles` are on an enabled Schedule — the figure that turns
   *  "editing to redo" into "files republishing with nobody watching". */
  scheduled_count: number;
  profiles: {
    id: string;
    name: string;
    league: string;
    season: number;
    sport: string;
    schedule_enabled: boolean;
    schedule_cron: string | null;
    eligibility: boolean;
  }[];
  /** Other entries in the sport whose per-90 derivation names this key. */
  derived_keys: string[];
}

/**
 * What may be corrected on an entry that exists.
 *
 * `sport` and `id` are excluded rather than merely unused: the server refuses
 * unknown patch fields outright, and `sport` is unpatchable on purpose — a
 * football Binding does not become a soccer Binding, so moving an entry between
 * sports would orphan every Binding naming it with nowhere for the cascade to
 * follow.
 */
export type StatEntryPatch = Partial<Omit<StatEntry, "id" | "sport">>;

export interface Binding {
  id: string;
  path: string;
  role: Role;
  stat_key: string | null;
  parse_rule: Record<string, unknown>;
  reviewed: boolean;
  // The Feed this Binding reads from, or null to read every Feed. Where two of
  // them answer, both answers are published, one column each (ADR-0055). It
  // must survive a round trip through this screen: a save that dropped it would
  // silently unscope every Binding and rewrite the column headers of the
  // published file.
  feed_id: string | null;
  /**
   * What this statistic is worth in the Impact Factor, relative to the others
   * this profile binds (ADR-0040). Null is nothing declared, which weighs 1 —
   * and is not the same review state as a declared 1, which is why the server
   * serves null rather than filling it in.
   *
   * On the Binding rather than on the Stat Dictionary entry, where Direction
   * and the Distribution live: those are properties of the statistic and
   * correcting one corrects every profile, where a weight is a judgement about
   * what matters in *this* ranking. Soccer's dictionary is shared by Wyscout,
   * the six ESPN leagues and MLS NEXT Pro.
   */
  weight: number | null;
  /**
   * Where this field's column sits in the published header (ADR-0058). Null is
   * nothing declared, which sorts after everything declared — not position
   * nought, and not the same review state as a declared 1.
   *
   * Numbered from 1 within its block: the statistics and the carried fields are
   * separate runs of columns in the raw file, so a carried field at 1 is first
   * among the carried rather than first in the file. `src/column-order.ts` is
   * the mirror of the server's rule and the only place either is applied.
   */
  sort_order: number | null;
}

/**
 * A Binding on its way to the server.
 *
 * The Feed reference is optional because the server defaults it to "every
 * Feed" — which is what a screen that does not offer Feed scoping means, and
 * what every profile written before scoping existed already has.
 *
 * `sort_order` is optional on the same terms: omitting it means nothing
 * declared, which is what a client that does not offer the Order column means
 * and what every Binding written before the column existed already holds.
 */
export type BindingDraft = Omit<Binding, "id" | "feed_id" | "weight" | "sort_order"> &
  Partial<Pick<Binding, "feed_id" | "weight" | "sort_order">>;

/** One value a low-cardinality field takes, and how often. */
export interface FieldValue {
  value: string;
  records: number;
}

export interface DiscoveredField {
  path: string;
  block: string;
  occurrences: number;
  samples: unknown[];
  /**
   * The distinct textual values this field takes, commonest first, where it has
   * few enough of them to be a vocabulary.
   *
   * **Distinct, where `samples` is the first three seen.** Three samples of a
   * position field are `QB`, `QB`, `QB`; this is what the publisher's positions
   * actually are — the question the add-a-group dialog asks on Add Source,
   * where the profile does not exist and has therefore never published the
   * labels a Run would record.
   *
   * Empty for a numeric field and for one with more values than the server's
   * cap, rather than truncated: a thousand distinct values is a player name.
   */
  values: FieldValue[];
  looks_numeric: boolean;
  suggested_role: Role;
  suggested_stat_key: string | null;
  /** The weight the Stat Dictionary declares for the suggested statistic, or null
   * where it declares none — so a review starts from what somebody filed against
   * the statistic rather than from blank. For a field already bound this is the
   * *Binding's* weight, because a re-read must not propose undoing a review. */
  suggested_weight: number | null;
  already_bound: boolean;
  /** Which Feed this field was read from. Null when discovery was given a bare
   * address — the Add Source screen, where there are no Feeds to scope to. */
  feed_id: string | null;
  feed_label: string | null;
  /** Whether to bind this field to that Feed alone. True where more than one
   * Feed publishes the path and nothing declares how the several values
   * reconcile, which is when merging them means the last Feed wins. */
  suggested_scoped: boolean;
}

export interface DiscoveredFeed {
  id: string;
  label: string | null;
  url: string;
  array_path: string;
  record_count: number;
}

/**
 * One Feed, and what it was last seen publishing (ADR-0061).
 *
 * `fields` is each path that Feed answered for with the first few values it
 * gave, relative to the profile's scope — so it compares directly against
 * `Binding.path`.
 *
 * `read_at` is null for a Feed nothing has read since this shipped, and it is
 * the field that tells *this Feed publishes nothing* from *nobody has looked*.
 * Both are an empty `fields`, and only the second is a reason to ask for a
 * re-read.
 */
export interface PublishedFieldsFeed {
  feed_id: string;
  label: string | null;
  fields: Record<string, unknown[]>;
  read_at: string | null;
}

export interface PublishedFields {
  feeds: PublishedFieldsFeed[];
}

/** One list the players might be in: its JSON path ("" is the document root). */
export interface RecordArray {
  path: string;
  record_count: number;
}

export interface DiscoveryResult {
  url: string;
  array_path: string;
  /** Across every Feed read. The per-Feed counts are on `feeds`. */
  record_count: number;
  blocks: string[];
  /** Every list of objects in the payload, largest first — the "Where are the
   * players?" menu. Optional so a response from an older server still reads. */
  record_arrays?: RecordArray[];
  fields: DiscoveredField[];
  new_field_count: number;
  /** The Feeds that were read, in the profile's order. Empty for a bare address. */
  feeds: DiscoveredFeed[];
}

export interface PreviewResult {
  rows: {
    identity: string;
    name: string | null;
    team: string | null;
    position: string | null;
    /** Null unless a Binding asked for one. The preview is the evidence a
     * mapping is judged by, so a role whose whole purpose is a published
     * column has to be visible before the file is written. */
    draft_season: string | null;
    canonical_position_group: string;
    stats: Record<string, number>;
    /** Fields bound as `carried`, under the keys the raw file publishes them
     * at. Apart from `stats` because nothing here reaches the ranker. */
    carried: Record<string, unknown>;
  }[];
  stat_keys: string[];
  records_seen: number;
  /** The raw file's carried columns, in the order it would publish them. */
  carried_columns: string[];
}

export interface StorageConfig {
  provider: "local" | "s3";
  bucket: string | null;
  prefix: string | null;
  region: string | null;
  local_root: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface StorageValidation {
  ok: boolean;
  reachable: boolean;
  writable: boolean;
  message: string;
}

export interface FailureReason {
  code: string;
  message: string;
  hint: string;
}

export interface SourceProfileInput {
  name: string;
  league: string;
  sport?: string;
  season: number;
  season_active?: boolean;
  identity_key: string[];
  feeds: FeedInput[];
  /**
   * The field review, saved with the profile in one request.
   *
   * Optional because the pasted-URL route may save before reviewing anything,
   * and because `PUT .../bindings` remains how the Fields screen edits a saved
   * profile. What it replaces is Add Source creating the profile and *then*
   * sending its Bindings: a refused `stat_key` left the profile committed, and
   * the retry collided with it.
   */
  bindings?: BindingDraft[];
  /**
   * What a repeated Identity Key inside one Feed means. Omitted is `fail`,
   * which is what the server defaults to and what every profile that does not
   * declare one gets. Arrives in a template's `defaults` where the publisher
   * has been measured repeating rows, and is editable on both field-mapping
   * screens because any publisher can start.
   */
  repeated_identity?: RepeatedIdentity;
  scope?: string | null;
  record_path?: string | null;
  eligibility_stat_key?: string | null;
  eligibility_min_value?: number;
  min_bound_stats?: number;
  /** Which ranking method to rank with. Omitted is the portal's current one.
   * Arrives in a template's `defaults` rather than being typed. */
  ranking_method?: string | null;
  required_columns?: string[];
  min_players?: number;
  max_players?: number;
  request_budget?: number;
  request_delay_ms?: number;
  /**
   * The Client Identity this profile presents — its `User-Agent` (ADR-0015).
   * Null or omitted is the portal's honest default, which is what almost every
   * profile should keep. The override exists because a publisher's CDN can
   * allowlist identities and refuse an honest one on an endpoint that needs no
   * account at all.
   */
  user_agent?: string | null;
  /**
   * How to keep only this division's players where the publisher will not
   * filter to it server-side. Composed by a Source Template, never typed —
   * PFF's `division` parameter accepts FBS and FCS and nothing else, so DII
   * and DIII are separated by team membership after the fetch.
   */
  team_filter?: Record<string, unknown> | null;
  position_map?: Record<string, string>;
  template_key?: string | null;
  template_parameters?: TemplateValues;
  source_slug?: string | null;
  /** A Credential Set created before this profile existed, for it to adopt.
   * Add Source signs in first where the template says the source needs it, so
   * that the fields it reads are the ones the subscription pays for. */
  credential_set_id?: string | null;
}

// --- Dashboard -------------------------------------------------------------

/** The windows the Dashboard offers, in days. The server refuses any other. */
export type DashboardDays = 1 | 7 | 30;

/**
 * One Run, named after the scraper it belongs to — the Dashboard's row shape for
 * what is running, what is failing, and what happened recently.
 */
export interface DashboardRun {
  run_id: string;
  /** Which screen the scraper lives on. */
  kind: "source" | "transfer_portal";
  profile_id: string;
  name: string;
  sport: string;
  status: string;
  phase: string | null;
  trigger: string;
  feeds_fetched: number;
  feeds_total: number;
  player_count: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  failure_code: string | null;
  failure_message: string | null;
  failure_hint: string | null;
  /** Running far longer than this scraper usually takes — probably hung. */
  stuck: boolean;
  /** The median of its recent successful runs, where it has any. */
  typical_duration_ms: number | null;
}

/** A schedule whose time came and went without the timer acting on it. */
export interface DashboardMissedSchedule {
  kind: "source" | "transfer_portal";
  profile_id: string;
  name: string;
  sport: string;
  cron: string;
  /** The first firing that was missed. */
  expected_at: string;
  last_fired_at: string | null;
}

/** A saved sign-in whose last attempt failed, and the scrapers relying on it. */
export interface DashboardSignInProblem {
  credential_id: string;
  name: string;
  code: string | null;
  message: string | null;
  updated_at: string;
  scrapers: DashboardScraperRef[];
}

export interface DashboardScraperRef {
  profile_id: string;
  name: string;
  kind: "source" | "transfer_portal";
}

/** A scheduled scraper and the next time its schedule fires. */
export interface DashboardUpcoming {
  kind: "source" | "transfer_portal";
  profile_id: string;
  name: string;
  sport: string;
  cron: string;
  next_run_at: string;
}

/** One Failure Reason, and how often the window's runs failed for it. */
export interface DashboardFailureReason {
  /** Null for a failure no Failure Reason classified. */
  code: string | null;
  /** The reason's general wording and remedy, not any one run's message. */
  message: string;
  hint: string | null;
  runs: number;
  scrapers: DashboardScraperRef[];
  latest_run_id: string;
  latest_at: string;
}

export interface DashboardBucket {
  start: string;
  succeeded: number;
  failed: number;
  other: number;
}

export interface DashboardSport {
  sport: string;
  scrapers: number;
  leagues: number;
  failing: number;
  succeeded: number;
  failed: number;
  success_rate: number | null;
  players: number;
}

export interface ScraperDashboard {
  generated_at: string;
  days: DashboardDays;
  since: string;
  bucket: "hour" | "day";
  /** The zone the buckets were cut in. Each `start` is still a UTC instant. */
  timezone: string;
  now: { running: number; queued: number };
  /** Runs that finished inside the window. Dry Runs are not counted. */
  period: {
    finished: number;
    succeeded: number;
    failed: number;
    other: number;
    success_rate: number | null;
    average_duration_ms: number | null;
  };
  buckets: DashboardBucket[];
  scrapers: {
    total: number;
    scheduled: number;
    failing: number;
    healthy: number;
    never_run: number;
    /** Scrapers with any listed problem, each counted once. */
    needs_attention: number;
    sports: number;
    leagues: number;
    players: number;
  };
  sports: DashboardSport[];
  transfer_portal: {
    total: number;
    scheduled: number;
    failing: number;
    succeeded: number;
    failed: number;
  };
  active: DashboardRun[];
  attention: DashboardRun[];
  missed_schedules: DashboardMissedSchedule[];
  sign_in_problems: DashboardSignInProblem[];
  upcoming: DashboardUpcoming[];
  failure_reasons: DashboardFailureReason[];
}

/**
 * One page of finished runs, newest first. Runs are never deleted while their
 * scraper exists, so the history is paged rather than cut off.
 */
export interface DashboardActivity {
  items: DashboardRun[];
  page: number;
  page_size: number;
  /** Every finished run, across all pages. */
  total: number;
}

// --- Endpoints -------------------------------------------------------------

/**
 * What a field read asks for.
 *
 * Named rather than inline because three things take it now: the
 * synchronous `discover`, the job that replaced it, and the await around
 * the job. A second spelling of this would be a second thing to keep in
 * step with `DiscoveryRequest` on the server.
 */
export interface DiscoveryBody {
    /** Omitted to read every Feed on the profile, which is what the field
     * review asks for: a source's fields are all of its reports' fields. */
    url?: string;
    /** With a bare URL, POST Feeds need the body the template composed. */
    method?: "GET" | "POST";
    body?: Record<string, unknown> | unknown[] | null;
    /**
     * And, on the same terms, how that address's coded statistics are decoded.
     *
     * Load-bearing on precisely one screen, and it is the first one. Add Source
     * has no saved profile to enumerate Feeds from, so it reads the addresses
     * the composition handed it — and an InStat address read without this
     * offers a reviewer the twelve identity and timing fields a record carries,
     * with not one statistic among them.
     */
    decoding?: Decoding | Record<string, never>;
    /**
     * And, on the same terms again, the address each record expands into.
     *
     * Load-bearing for one publisher, and it is the one whose statistics do
     * not otherwise exist: ESPN soccer's roster carries identity, bio and
     * position and no statistics whatsoever — measured on MLS, thirty-two
     * athletes and not one `statistics` key — while the per-athlete address
     * behind each record answers with 107. Read without this, the field review
     * offers forty-three biographical fields, and nothing can be bound to a
     * path discovery never named.
     */
    expansion?: Expansion | Record<string, never>;
    source_profile_id?: string;
    scope?: string | null;
    record_path?: string | null;
    /** Resolves a `{season}` placeholder in `url` when no profile exists yet —
     * the Add Source screen, where the season is a form value rather than a
     * stored column. Omitted for a saved profile, whose own season is used. */
    season?: number | null;
    /** Sign in with this Credential Set before reading. Load-bearing on a
     * source that sells columns rather than access: PFF answers an anonymous
     * read with every player and none of the grades, so a review done without
     * it binds the wrong fields and cannot be told apart from a healthy one. */
    credential_set_id?: string | null;
    /** Read with this Client Identity. Present on this route because discovery
     * is the first request Add Source makes: without it the override could only
     * ever be set on a profile that could never be created (ADR-0018). */
    user_agent?: string | null;
    /** Which sport's Stat Dictionary to suggest against when no profile exists
     * yet. Required on Add Source so a basketball leaf cannot bind a football
     * entry (ADR-0017). */
    sport?: string | null;
}

export const api = {
  // Google's identity token, exchanged at the door for the portal's own
  // session cookie. Nothing about the token is stored here — it is sent once
  // and the response is an ordinary signed-in User.
  googleSignIn: (credential: string) => post<User>("/auth/google", { credential }),
  signInMethods: () => get<SignInMethods>("/auth/methods"),
  login: (body: { email: string; password: string }) => post<User>("/auth/login", body),
  logout: () => post<void>("/auth/logout"),
  me: () => get<User>("/auth/me"),

  updateAccount: (body: { display_name?: string; email?: string }) => patch<User>("/account", body),
  changePassword: (body: { current_password: string; new_password: string }) =>
    post<{ sessions_invalidated: number }>("/account/password", body),

  /** `timeZone` is the viewer's IANA zone, so the chart's days are theirs. */
  dashboard: (days: DashboardDays, timeZone: string) =>
    get<ScraperDashboard>(`/dashboard?days=${days}&tz=${encodeURIComponent(timeZone)}`),
  dashboardActivity: (page: number, pageSize: number) =>
    get<DashboardActivity>(`/dashboard/activity?page=${page}&page_size=${pageSize}`),

  listProfiles: () => get<SourceProfile[]>("/source-profiles"),
  createProfile: (body: SourceProfileInput) => post<SourceProfile>("/source-profiles", body),
  readProfile: (id: string) => get<SourceProfile>(`/source-profiles/${id}`),
  updateProfile: (id: string, body: Partial<SourceProfileInput>) =>
    patch<SourceProfile>(`/source-profiles/${id}`, body),
  deleteProfile: (id: string) => del(`/source-profiles/${id}`),

  readSchedule: (id: string) => get<Schedule>(`/source-profiles/${id}/schedule`),
  /**
   * Setting a cron expression is always allowed; enabling one is not. The
   * server answers 409 `dry_run_required` when no passing dry run describes
   * this configuration (D20).
   */
  setSchedule: (id: string, body: { cron: string | null; enabled: boolean }) =>
    put<Schedule>(`/source-profiles/${id}/schedule`, body),

  listTemplates: () => get<SourceTemplate[]>("/source-templates"),
  /**
   * The same catalogue read by league. Composes nothing on the server, so it
   * answers immediately and reaches no publisher.
   */
  listLeagues: () => get<LeagueMenuEntry[]>("/source-templates/leagues"),
  /**
   * The Feeds these values produce. A read: it creates nothing, and the form
   * calls it on every change so that the Feed list on screen is the one that
   * would be saved.
   */
  composeTemplate: (key: string, parameters: TemplateValues) =>
    post<TemplateComposition>(`/source-templates/${key}/compose`, { parameters }),

  listTransferPortalTemplates: () => get<TransferPortalTemplate[]>("/transfer-portal/templates"),
  /**
   * The profile fields these values produce. A read: it creates nothing, and
   * the form calls it on every change so that the League and the two addresses
   * on screen are the ones that would be saved.
   */
  composeTransferPortalTemplate: (key: string, parameters: TemplateValues) =>
    post<TransferPortalComposition>(`/transfer-portal/templates/${key}/compose`, { parameters }),

  listTransferPortalProfiles: () => get<TransferPortalProfile[]>("/transfer-portal/profiles"),
  createTransferPortalProfile: (body: Record<string, unknown>) =>
    post<TransferPortalProfile>("/transfer-portal/profiles", body),
  readTransferPortalProfile: (id: string) =>
    get<TransferPortalProfile>(`/transfer-portal/profiles/${id}`),
  deleteTransferPortalProfile: (id: string) => del(`/transfer-portal/profiles/${id}`),
  readTransferPortalSchedule: (id: string) =>
    get<TransferPortalSchedule>(`/transfer-portal/profiles/${id}/schedule`),
  setTransferPortalSchedule: (id: string, body: { cron: string | null; enabled: boolean }) =>
    put<TransferPortalSchedule>(`/transfer-portal/profiles/${id}/schedule`, body),
  startTransferPortalRun: (id: string) => post<Run>(`/transfer-portal/profiles/${id}/runs`, {}),

  listBindings: (id: string) => get<Binding[]>(`/source-profiles/${id}/bindings`),
  /**
   * What each Feed was last seen publishing (ADR-0061).
   *
   * Reaches no publisher — a stored reading, which is the point: it is what
   * lets the Fields screen offer its per-report disclosure on load instead of
   * only in the seconds after a three-minute serial re-read.
   */
  listPublishedFields: (id: string) =>
    get<PublishedFields>(`/source-profiles/${id}/published-fields`),
  replaceBindings: (id: string, bindings: BindingDraft[]) =>
    put<Binding[]>(`/source-profiles/${id}/bindings`, { bindings }),
  /**
   * A sample of parsed rows. Given Bindings, they are previewed *instead of*
   * the saved ones and nothing is written — which is the only way to judge a
   * scope before adopting it, since adopting one renames a published column.
   */
  preview: (id: string, bindings?: BindingDraft[]) =>
    post<PreviewResult>(`/source-profiles/${id}/preview`, bindings ? { bindings } : {}),
  discover: (body: DiscoveryBody) =>
    post<DiscoveryResult>("/discovery", body),

  /**
   * Start a field read, and answer with the id to poll.
   *
   * **This is what the screens call**, and `discover` above is what they used
   * to. A read of PFF's FCS template takes minutes — `defense/pass_rush` alone
   * was measured at about two — and a Netlify proxy rewrite gives any one
   * request twenty-six seconds, so the read cannot be the request.
   *
   * Submitting a read that is already running returns **that** read rather
   * than starting a second: the server joins by fingerprint. That is what
   * makes `withRetry` safe in front of this, and it is the direct fix for the
   * pileup the logs showed — the edge answering early, the browser asking
   * again, and two chains against one PFF address interleaving.
   */
  startDiscoveryJob: (body: DiscoveryBody) =>
    post<DiscoveryJob<DiscoveryResult>>("/discovery/jobs", body),
  readDiscoveryJob: (jobId: string) =>
    get<DiscoveryJob<DiscoveryResult>>(`/discovery/jobs/${jobId}`),

  /** Store a credential that no profile owns yet, and get its id back.
   * The Add Source screen passes that id to `discover` and then to
   * `createProfile`, which is what lets the sign-in come first.
   * Optional `probe` proves a bearer (or other static secret) against the
   * composed Feed before it is stored — wrong tokens never unlock discovery. */
  createCredential: (body: {
    name: string;
    credential_type: CredentialType;
    /** Omitted only in company with `from_template`, which is the one case
     * where the server already holds the secret. */
    secret?: Record<string, unknown>;
    /** Build the secret from the deployment's own configuration rather than
     * sending one. Posted instead of `secret` when the template reported
     * `credential_preconfigured`, so a key set on the deployment never has to
     * be sent to a browser in order to be sent back.
     *
     * The server reads the `Settings` field that template names, and refuses
     * with a 422 if it is empty — which is what makes "the form stopped
     * asking" and "there is a key" the same fact rather than two. */
    from_template?: string;
    /** What the template declared about signing in, passed straight through.
     * Omitted leaves the deployment default to decide (ADR-0023). */
    session_acquirer?: string | null;
    probe?: {
      url: string;
      method?: "GET" | "POST";
      body?: Record<string, unknown> | unknown[] | null;
      season?: number | null;
    };
  }) => post<CredentialSummary>("/credentials", body),

  /** One Credential Set, so a sign-in in flight can be polled (ADR-0033).
   *  Creating a `login_form` credential returns before the browser has run. */
  readCredential: (id: string) => get<CredentialSummary>(`/credentials/${id}`),

  /** Throw away a Credential Set no profile has adopted — in practice, a
   *  sign-in that was refused. Kept while the screen is showing the reason it
   *  failed; removed when the operator moves on, so a mistyped password does
   *  not leave an unusable Credential Set in the Workspace forever.
   *
   *  Named apart from `deleteCredential`, which is the profile-scoped route:
   *  one detaches a credential from a source that owns it, the other discards
   *  one nothing owns. Sharing a name would have made them look interchangeable
   *  from the call site, and they are not. */
  discardCredential: (id: string) => del(`/credentials/${id}`),

  saveCredential: (
    id: string,
    body: {
      name: string;
      credential_type: CredentialType;
      /** Omitted only in company with `from_template`. */
      secret?: Record<string, unknown>;
      /** Take the secret from the deployment's own configuration instead of
       * sending one — the same mechanism `createCredential` uses, and the way
       * an existing profile picks up a key that was rotated in the
       * environment. A posted `secret` wins, so pasting a different key is
       * always available. */
      from_template?: string;
      session_acquirer?: string | null;
    },
  ) => put<CredentialSummary>(`/source-profiles/${id}/credential`, body),
  deleteCredential: (id: string) => del(`/source-profiles/${id}/credential`),
  testCredential: (id: string) =>
    post<{ ok: boolean; status: number | null; code: string | null; message: string }>(
      `/source-profiles/${id}/credential/test`,
    ),

  startRun: (id: string, testMode: boolean) =>
    post<Run>(`/source-profiles/${id}/runs`, { test_mode: testMode }),
  listRuns: (id: string) => get<Run[]>(`/source-profiles/${id}/runs`),
  readRun: (runId: string) => get<Run>(`/runs/${runId}`),
  cancelRun: (runId: string) => post<Run>(`/runs/${runId}/cancel`),
  downloadUrl: (runId: string, filename: string) =>
    `${BASE}/runs/${runId}/downloads/${encodeURIComponent(filename)}`,
  failureReasons: () => get<FailureReason[]>("/failure-reasons"),

  listStats: (sport?: string) =>
    get<StatEntry[]>(sport ? `/stat-dictionary?sport=${encodeURIComponent(sport)}` : "/stat-dictionary"),
  /** The sports the dictionary actually holds entries for, for the filter. */
  listStatSports: () => get<string[]>("/stat-dictionary/sports"),
  /**
   * The Canonical Position Groups each sport offers, for the *Positions Groups
   * Excluded* control.
   *
   * Every sport in one document because the dictionary table shows six of them
   * at once and a row's options are its own sport's — football has no `GK` and
   * the server refuses a football statistic that excludes one. Groups rather
   * than the publishers' labels: a cohort is built from the canonical group
   * (ADR-0026), so `DB`, `CB`, `S`, `FS` and `SS` are one option.
   */
  listPositionGroups: () =>
    get<PositionGroups>("/stat-dictionary/position-groups"),
  /**
   * The raw position labels a source actually published, most common first.
   *
   * Read off its latest published Run. The evidence behind adding a Canonical
   * Position Group: the labels landing in `UNKNOWN` are the ones nothing maps,
   * and mapping one is what brings a new group into existence. Empty for a
   * profile that has never published — it has sent nothing, which is a state to
   * render rather than an error.
   */
  listPositionLabels: (profileId: string) =>
    get<PublishedPositionLabels>(`/source-profiles/${profileId}/position-labels`),
  listVocabularies: () => get<Vocabulary[]>("/stat-dictionary/vocabularies"),
  installVocabulary: (key: string) =>
    post<VocabularyInstall>(`/stat-dictionary/vocabularies/${key}/install`),
  createStat: (body: {
    sport?: string;
    key: string;
    display_name: string;
    direction: Direction;
    /** Omitted rather than sent as null where nothing was typed, so the server
     *  records "nothing declared" rather than a decision nobody made. */
    default_weight?: number;
    aliases?: string[];
    /** Canonical Position Groups this statistic does not rank. Omitted rather
     *  than sent empty where nothing was chosen, which is the same thing to the
     *  server and keeps the body to what was decided. */
    excluded_position_groups?: string[];
  }) => post<StatEntry>("/stat-dictionary", body),
  /**
   * Correct an entry. `StatEntryPatch` rather than `Partial<StatEntry>`, which
   * admitted `id` and `sport` — both of which the server now refuses outright,
   * so the loose type turned a refusal into a compile-time silence.
   */
  updateStat: (id: string, body: StatEntryPatch) => patch<StatEntry>(`/stat-dictionary/${id}`, body),
  /** What a rename would carry across, read before the rename is saved. */
  statUsage: (id: string) => get<StatUsage>(`/stat-dictionary/${id}/usage`),
  deleteStat: (id: string) => del(`/stat-dictionary/${id}`),

  readStorage: () => get<StorageConfig>("/settings/storage"),
  updateStorage: (body: Omit<StorageConfig, "updated_at" | "updated_by">) =>
    put<StorageConfig>("/settings/storage", body),
  validateStorage: () => post<StorageValidation>("/settings/storage/validate"),
  /**
   * The paths a Run would write to a destination that has not been saved yet,
   * so the preview follows what is being typed rather than what is stored.
   */
  previewStorage: (body: Omit<StorageConfig, "updated_at" | "updated_by">) =>
    post<StoragePreview>("/settings/storage/preview", body),
};
